import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";

import { Model } from "../../utils/model";
import { Option } from "../../utils/option";
import { Loader } from "../Loader";
import {
    sumDiscountPrice,
    sumDiscountTotal,
    sumPrice,
    sumTotal,
    subTotal,
    getOptionData1,
} from "../../utils/quote";
import { ModelOptionDataType } from "../../types/global.types";

type Props = {
    quote?: any;
    companyInfo?: any;
    options: Option[];
    models: Model[];
    priceType?: string;
};

export const PDFContent: React.FC<Props> = ({
    quote,
    companyInfo,
    models,
    options,
    priceType = "dealer_price",
}) => {
    const [data, setData] = useState<any>([]);

    useEffect(() => {
        const modelOptionData = quote?.model_option_ids
            ? JSON.parse(quote?.model_option_ids)
            : [];

        const _modelOptionData = modelOptionData?.map(
            (res: ModelOptionDataType) => {
                let _model = models.find(
                    (model: Model) => model.ID === res.model_id?.ID
                );

                return {
                    model: {
                        model_name: _model?.model_name,
                        part_number: _model?.part_number,
                        dealer_price: res.model_id?.[priceType],
                        MSRP: res.model_id?.MSRP,
                    },
                    options: getOptionData1(options, res.option_ids),
                    qty: res.qty,
                };
            }
        );

        setData(_modelOptionData);
    }, [quote, options, models, companyInfo]);

    const quoteExpirationDate = useMemo(() => {
        if (!companyInfo?.createdAt) return ""; // Return empty if no date available

        let date = new Date(quote?.createdAt); // Parse the ISO date string

        const expirationNumber =
            Number(companyInfo?.quote_expiration_setting_number) || 0; // Ensure it's a number

        if (companyInfo?.quote_expiration_setting_date === "month") {
            date.setMonth(date.getMonth() + expirationNumber);
        } else if (companyInfo?.quote_expiration_setting_date === "year") {
            date.setFullYear(date.getFullYear() + expirationNumber);
        } else {
            date.setDate(date.getDate() + expirationNumber); // Fix: Use getDate() instead of getDay()
        }
        return moment(date).format("MM/DD/YYYY");
    }, [companyInfo, quote]);

    const checkZero = useCallback((val: number | undefined | null): string => {
        if (!val || val === 0) return "0";
        return `$${val.toFixed(2)}`;
    }, []);

    if (!data) return <Loader isLoading={!data} />;

    return (
        <div
            style={{
                fontFamily: "Arial, Helvetica, sans-serif",
                fontSize: 12,
                // padding: ".4in .3in",
            }}
        >
            <div
                style={{
                    background: "#000",
                    color: "#FFF",
                    paddingLeft: "0.3in",
                    position: "relative",
                }}
            >
                <h3
                    style={{
                        float: "left",
                        width: "50%",
                        boxSizing: "border-box",
                        lineHeight: "35px",
                    }}
                >
                    {priceType === "dealer_price" ? `${process.env.REACT_APP_DEALER_NAME} Quote # ` : "MSRP Quote # "}
                    {quote?.no}
                </h3>
                <img
                    src={
                        typeof companyInfo?.company_logo === "string"
                            ? `${process.env.REACT_APP_COMPANY_LOGO_URL}/${companyInfo?.company_logo}`
                            : undefined
                    }
                    style={{
                        display: "inline-block",
                        width: "auto",
                        height: "100px",
                        position: "absolute",
                        left: "calc(50% - 55px)",
                        objectFit: "cover",
                    }}
                />
                <h4
                    style={{
                        float: "left",
                        width: "50%",
                        boxSizing: "border-box",
                        textAlign: "center",
                        lineHeight: "36px",
                    }}
                >
                    Generated: {moment(quote.createdAt).format("MM/DD/YYYY")}
                </h4>
                <div style={{ clear: "both" }}></div>
            </div>
            <div style={{ padding: "0 .3in" }}>
                <div className="customer-info" style={{ margin: "15px 0" }}>
                    <div style={{ float: "left", width: "66.66%" }}>
                        <div>
                            {companyInfo?.dealer_name || companyInfo?.company_name}
                        </div>
                        <div>{companyInfo?.contact_name}</div>
                        <div>{companyInfo?.address}</div>
                        <div>
                            {companyInfo?.city}, {companyInfo?.state}, {companyInfo?.zip}
                        </div>
                        <div>{companyInfo?.country}</div>
                    </div>
                    <div style={{ float: "left", width: "33.33%" }}>
                        {quote?.opt_reference ? "Opt. Reference: " : ""}{quote?.opt_reference}
                    </div>
                    <div style={{ clear: "both" }} />
                </div>
                <table
                    style={{
                        borderCollapse: "collapse",
                        width: "100%",
                        textAlign: "left",
                        fontFamily: "Arial, Helvetica, sans-serif",
                        fontSize: 8,
                    }}
                >
                    <thead>
                        <tr>
                            <th
                                style={{
                                    border: "1px solid #ddd",
                                    padding: 5,
                                    background: "black",
                                    color: "white",
                                }}
                            >
                                BF Part #
                            </th>
                            <th
                                style={{
                                    border: "1px solid #ddd",
                                    padding: 5,
                                    background: "black",
                                    color: "white",
                                }}
                            >
                                Product Name
                            </th>
                            <th
                                style={{
                                    border: "1px solid #ddd",
                                    padding: 5,
                                    background: "black",
                                    color: "white",
                                }}
                            >
                                Qty
                            </th>
                            <th
                                style={{
                                    border: "1px solid #ddd",
                                    padding: 5,
                                    background: "black",
                                    color: "white",
                                }}
                            >
                                {priceType === "MSRP" ? priceType : "Price"}
                            </th>
                            <th
                                style={{
                                    border: "1px solid #ddd",
                                    padding: 5,
                                    background: "black",
                                    color: "white",
                                }}
                            >
                                Total
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data?.map(({ model, options, qty }: any, index: number) => (
                            <>
                                <tr style={{ borderBottom: "1px solid" }}>
                                    <td>{model?.part_number}</td>
                                    <td>Model - {model?.model_name}</td>
                                    {/* <td>{qty}</td> */}
                                    <td>1</td>
                                    <td>{checkZero(model?.[priceType])}</td>
                                    {/* <td>${(model?.[priceType] * qty)?.toFixed(2)}</td> */}
                                    <td>{checkZero(model?.[priceType])}</td>
                                </tr>
                                {/* creates multiple identical line items logic */}
                                {options &&
                                    options.length > 0 &&
                                    options.map((res: any, no: number) =>
                                        // Array.from({ length: res?.QTYNumber }, (_, index) => (
                                        <tr key={`${res?.part_number}-${no}`} style={{ borderBottom: "1px solid" }}>
                                            <td style={{ paddingLeft: 10 }}>{res?.part_number}</td>
                                            <td style={{ paddingLeft: 10 }}>{no + 1}) Opt. {res?.option_name}</td>
                                            {/* <td>{qty}</td> */}
                                            <td>{res?.QTYNumber}</td>
                                            <td>{checkZero(res?.[priceType])}</td>
                                            {/* <td>${(res?.[priceType] * qty)?.toFixed(2)}</td> */}
                                            <td>{checkZero(res?.[priceType] * (res?.QTYNumber || 1))}</td>
                                        </tr>
                                        // ))
                                    )}

                                {((process.env.REACT_APP_SERVER_TYPE === "spacex" || process.env.REACT_APP_SERVER_TYPE === "industrial") || process.env.REACT_APP_SERVER_TYPE === "industrial") ? (
                                    <>
                                        {qty >= 10 && qty <= 24 && (
                                            <tr>
                                                <td>Volume Discount 10-24 Unit</td>
                                                <td>{`${companyInfo?.qty_discount_1}%`}</td>
                                                {/* <td>{qty}</td> */}
                                                <td>1</td>
                                                <td>
                                                    $-
                                                    {sumDiscountPrice(
                                                        model,
                                                        options,
                                                        companyInfo?.qty_discount_1,
                                                        priceType
                                                    ).toFixed(2)}
                                                </td>
                                                <td>
                                                    $-
                                                    {sumDiscountTotal(
                                                        model,
                                                        options,
                                                        qty,
                                                        companyInfo?.qty_discount_1,
                                                        priceType
                                                    ).toFixed(2)}
                                                </td>
                                            </tr>
                                        )}
                                        {qty >= 25 && qty <= 49 && (
                                            <tr>
                                                <td>Volume Discount 25-49 Unit</td>
                                                <td>{`${companyInfo?.qty_discount_2}%`}</td>
                                                {/* <td>{qty}</td> */}
                                                <td>1</td>
                                                <td>
                                                    $-
                                                    {sumDiscountPrice(
                                                        model,
                                                        options,
                                                        companyInfo?.qty_discount_2,
                                                        priceType
                                                    ).toFixed(2)}
                                                </td>
                                                <td>
                                                    $-
                                                    {sumDiscountTotal(
                                                        model,
                                                        options,
                                                        qty,
                                                        companyInfo?.qty_discount_2,
                                                        priceType
                                                    ).toFixed(2)}
                                                </td>
                                            </tr>
                                        )}
                                        {qty > 49 && (
                                            <tr>
                                                <td>Volume Discount 50+ Units</td>
                                                <td>{`${companyInfo?.qty_discount_3}%`}</td>
                                                {/* <td>{qty}</td> */}
                                                <td>1</td>
                                                <td>
                                                    $-
                                                    {sumDiscountPrice(
                                                        model,
                                                        options,
                                                        companyInfo?.qty_discount_3,
                                                        priceType
                                                    ).toFixed(2)}
                                                </td>
                                                <td>
                                                    $-
                                                    {sumDiscountTotal(
                                                        model,
                                                        options,
                                                        qty,
                                                        companyInfo?.qty_discount_3,
                                                        priceType
                                                    ).toFixed(2)}
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {qty >= 5 && qty <= 9 && (
                                            <tr>
                                                <td>Volume Discount 5-9 Unit</td>
                                                <td>{`${companyInfo?.qty_discount_1}%`}</td>
                                                {/* <td>{qty}</td> */}
                                                <td>1</td>
                                                <td>
                                                    $-
                                                    {sumDiscountPrice(
                                                        model,
                                                        options,
                                                        companyInfo?.qty_discount_1,
                                                        priceType
                                                    ).toFixed(2)}
                                                </td>
                                                <td>
                                                    $-
                                                    {sumDiscountTotal(
                                                        model,
                                                        options,
                                                        qty,
                                                        companyInfo?.qty_discount_1,
                                                        priceType
                                                    ).toFixed(2)}
                                                </td>
                                            </tr>
                                        )}
                                        {qty > 9 && (
                                            <tr>
                                                <td>Volume Discount 10+ Units</td>
                                                <td>{`${companyInfo?.qty_discount_2}%`}</td>
                                                {/* <td>{qty}</td> */}
                                                <td>1</td>
                                                <td>
                                                    $-
                                                    {sumDiscountPrice(
                                                        model,
                                                        options,
                                                        companyInfo?.qty_discount_2,
                                                        priceType
                                                    ).toFixed(2)}
                                                </td>
                                                <td>
                                                    $-
                                                    {sumDiscountTotal(
                                                        model,
                                                        options,
                                                        qty,
                                                        companyInfo?.qty_discount_2,
                                                        priceType
                                                    ).toFixed(2)}
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                )}
                                <tr className="system" style={{ fontSize: "7px" }}>
                                    <td
                                        colSpan={2}
                                        style={{
                                            border: "1px solid #ddd",
                                            padding: 3,
                                            background: "#282828",
                                            color: "white",
                                        }}
                                    >
                                        System {index + 1}
                                    </td>
                                    <td
                                        colSpan={2}
                                        style={{
                                            border: "1px solid #ddd",
                                            padding: 3,
                                            background: "#282828",
                                            color: "white",
                                            textAlign: "right"
                                        }}
                                    >
                                        Price per system:
                                    </td>
                                    <td
                                        style={{
                                            border: "1px solid #ddd",
                                            padding: 3,
                                            background: "#282828",
                                            color: "white",
                                        }}
                                    >
                                        $
                                        {(process.env.REACT_APP_SERVER_TYPE === "spacex" || process.env.REACT_APP_SERVER_TYPE === "industrial") ? (
                                            <>
                                                {qty >= 10 && qty <= 24 &&
                                                    (
                                                        sumPrice(model, options, priceType) -
                                                        sumDiscountPrice(
                                                            model,
                                                            options,
                                                            companyInfo?.qty_discount_1,
                                                            priceType
                                                        )
                                                    )?.toFixed(2)
                                                }
                                                {qty >= 25 && qty <= 49 && (
                                                    (
                                                        sumPrice(model, options, priceType) -
                                                        sumDiscountPrice(
                                                            model,
                                                            options,
                                                            companyInfo?.qty_discount_2,
                                                            priceType
                                                        )
                                                    )?.toFixed(2)
                                                )}
                                                {qty > 49 && (
                                                    (
                                                        sumPrice(model, options, priceType) -
                                                        sumDiscountPrice(
                                                            model,
                                                            options,
                                                            companyInfo?.qty_discount_3,
                                                            priceType
                                                        )
                                                    )?.toFixed(2)
                                                )}
                                                {qty < 10 && sumPrice(model, options, priceType)?.toFixed(2)}
                                            </>
                                        ) : (
                                            <>
                                                {qty >= 5 &&
                                                    qty <= 9 &&
                                                    (
                                                        sumPrice(model, options, priceType) -
                                                        sumDiscountPrice(
                                                            model,
                                                            options,
                                                            companyInfo?.qty_discount_1,
                                                            priceType
                                                        )
                                                    )?.toFixed(2)}
                                                {qty > 9 &&
                                                    (
                                                        sumPrice(model, options, priceType) -
                                                        sumDiscountPrice(
                                                            model,
                                                            options,
                                                            companyInfo?.qty_discount_2,
                                                            priceType
                                                        )
                                                    )?.toFixed(2)}
                                                {qty < 5 && sumPrice(model, options, priceType)?.toFixed(2)}
                                            </>
                                        )}
                                    </td>
                                </tr>
                                <tr className="system" style={{ fontSize: "7px" }}>
                                    <td
                                        colSpan={2}
                                        style={{
                                            border: "1px solid #ddd",
                                            padding: 3,
                                            background: "#282828",
                                            color: "white",
                                            textAlign: "right"
                                        }}
                                    >
                                        System Quantity: {qty}
                                    </td>
                                    <td
                                        colSpan={2}
                                        style={{
                                            border: "1px solid #ddd",
                                            padding: 3,
                                            background: "#282828",
                                            color: "white",
                                            textAlign: "right"
                                        }}
                                    >
                                        System {index + 1} Total:
                                    </td>
                                    <td
                                        style={{
                                            border: "1px solid #ddd",
                                            padding: 3,
                                            background: "#282828",
                                            color: "white",
                                        }}
                                    >
                                        $
                                        {(process.env.REACT_APP_SERVER_TYPE === "spacex" || process.env.REACT_APP_SERVER_TYPE === "industrial") ? (
                                            <>
                                                {qty >= 10 && qty <= 24 &&
                                                    (
                                                        sumTotal(model, options, qty, priceType) -
                                                        sumDiscountTotal(
                                                            model,
                                                            options,
                                                            qty,
                                                            companyInfo?.qty_discount_1,
                                                            priceType
                                                        )
                                                    )?.toFixed(2)
                                                }
                                                {qty >= 25 && qty <= 49 && (
                                                    (
                                                        sumTotal(model, options, qty, priceType) -
                                                        sumDiscountTotal(
                                                            model,
                                                            options,
                                                            qty,
                                                            companyInfo?.qty_discount_2,
                                                            priceType
                                                        )
                                                    )?.toFixed(2)
                                                )}
                                                {qty > 49 && (
                                                    (
                                                        sumTotal(model, options, qty, priceType) -
                                                        sumDiscountTotal(
                                                            model,
                                                            options,
                                                            qty,
                                                            companyInfo?.qty_discount_3,
                                                            priceType
                                                        )
                                                    )?.toFixed(2)
                                                )}
                                                {qty < 10 && sumTotal(model, options, qty, priceType)?.toFixed(2)}
                                            </>
                                        ) : (
                                            <>
                                                {qty >= 5 &&
                                                    qty <= 9 &&
                                                    (
                                                        sumTotal(model, options, qty, priceType) -
                                                        sumDiscountTotal(
                                                            model,
                                                            options,
                                                            qty,
                                                            companyInfo?.qty_discount_1,
                                                            priceType
                                                        )
                                                    )?.toFixed(2)}
                                                {qty > 9 &&
                                                    (
                                                        sumTotal(model, options, qty, priceType) -
                                                        sumDiscountTotal(
                                                            model,
                                                            options,
                                                            qty,
                                                            companyInfo?.qty_discount_2,
                                                            priceType
                                                        )
                                                    )?.toFixed(2)}
                                                {qty < 5 &&
                                                    sumTotal(model, options, qty, priceType)?.toFixed(2)}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            </>
                        ))}
                        <tr>
                            <td colSpan={3} rowSpan={3} style={{ fontSize: 10 }}>
                                {quote?.memo ? "Memo: " : ""}{quote?.memo}
                            </td>
                            <td
                                style={{
                                    border: "1px solid #ddd",
                                    padding: 5,
                                    background: "#282828",
                                    color: "white",
                                }}
                            >
                                Subtotal
                            </td>
                            <td
                                style={{
                                    border: "1px solid #ddd",
                                    padding: 5,
                                    background: "#282828",
                                    color: "white",
                                }}
                            >
                                ${subTotal(data, companyInfo, priceType)?.toFixed(2)}
                            </td>
                        </tr>
                        <tr>
                            <td
                                style={{
                                    border: "1px solid #ddd",
                                    padding: 5,
                                    background: "#282828",
                                    color: "white",
                                }}
                            >
                                Tax
                            </td>
                            <td
                                style={{
                                    border: "1px solid #ddd",
                                    padding: 5,
                                    background: "#282828",
                                    color: "white",
                                }}
                            >
                                0
                            </td>
                        </tr>
                        <tr>
                            <td
                                style={{
                                    border: "1px solid #ddd",
                                    padding: 5,
                                    background: "#000000",
                                    color: "white",
                                }}
                            >
                                Total
                            </td>
                            <td
                                style={{
                                    border: "1px solid #ddd",
                                    padding: 5,
                                    background: "#000000",
                                    color: "white",
                                }}
                            >
                                ${subTotal(data, companyInfo, priceType)?.toFixed(2)}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div className="notes">
                    <p>
                        Your quote is locked in for{" "}
                        {companyInfo?.quote_expiration_setting_number}{" "}
                        {companyInfo?.quote_expiration_setting_date}s.
                    </p>
                    <p>
                        Prices are subject to change <br />
                        after {quoteExpirationDate} in accordance <br />
                        with part and labor price fluctuations.
                    </p>
                </div>
                <div
                    className="footer"
                    id="pdfFooter"
                    style={{
                        textAlign: "center",
                        // position: "absolute",
                        // bottom: 0,
                        // left: 0,
                        // right: 0,
                    }}
                >
                    <p>
                        Bigfoot Mobile Systems - BigFootMobileSystems.com - 707.602.5548
                    </p>
                </div>
            </div>
        </div>
    );
};
